import { useRef, useCallback, useMemo, useSyncExternalStore } from 'react';
import { DndContext } from '@dnd-kit/core';
import { TimelineTrackElement } from './TimelineTrackElement';
import { SnapLine } from './components/SnapLine';
import { useElementLanesState } from './hooks/useElementLanesState';
import { useElementInteractions } from './hooks/useElementInteractions';
import { useTimelineElementDnD } from './hooks/useTimelineElementDnD';
import { useAiElementUpdates } from '../../ClipCustomizer/pocAIEdits/hooks/useAiElementUpdates';
import { ImageOverlaySelectionElementData } from '@/App/remotion/components/Outlines/types';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { useSelectionOutline } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useSelectionOutline';
import { getPlayerConfigElements } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils/playerConfigGetters';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

export default function TimelineTrackElementLanes() {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isOverlayLayeringEnabled = Boolean(featureFlags[FeatureFlagKeys.Use_CL_Overlay_Layering]);

  const lanesContainerRef = useRef<HTMLDivElement>(null);

  const { selectedElementData } = useSelectionOutline();
  const { clipData, clipId, fps } = useClipsContext();
  const { handleElementUpdate, handleElementDelete } = useAiElementUpdates();
  const { seekTo } = useRemotionPlayerSeek();

  const playerConfig = useMemo(() => clipData.asset_metadata.player_config, [clipData.asset_metadata.player_config]);
  const playerConfigElementTracks = useMemo(() => getPlayerConfigElements(playerConfig), [playerConfig]);

  const {
    elementLanes,
    setElementLanes,
    draggingElementId,
    setDraggingElementId,
    verticalDragState,
    setVerticalDragState,
    isVerticalDragging,
    setIsVerticalDragging,
    elementLanesRef,
    initialFramesRef,
    isUpdatingFromVerticalDragRef,
    resetDragState
  } = useElementLanesState(playerConfigElementTracks);

  const handleElementUpdateCallback = useCallback(
    (elementId: string, updates) => {
      handleElementUpdate(clipId, elementId, updates);
    },
    [handleElementUpdate, clipId]
  );

  const handleElementDeleteCallback = useCallback(
    (elementId: string) => {
      handleElementDelete(clipId, elementId);
    },
    [handleElementDelete, clipId]
  );

  const { sensors, snapModifier, handleDragStart, handleDragMove, handleDragEnd, handleDragCancel } =
    useTimelineElementDnD({
      fps,
      isOverlayLayeringEnabled,
      playerConfig: playerConfig || null,
      clipData,
      clipId,
      elementLanes,
      lanesContainerRef,
      elementLanesRef,
      initialFramesRef,
      isUpdatingFromVerticalDragRef,
      setDraggingElementId,
      setElementLanes,
      setIsVerticalDragging,
      setVerticalDragState,
      resetDragState
    });

  const { handleElementClick } = useElementInteractions({
    elementLanes,
    draggingElementId,
    fps,
    seekTo
  });

  if (!elementLanes.length) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[snapModifier]}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      autoScroll={{ layoutShiftCompensation: false, enabled: true }}
    >
      <div
        ref={lanesContainerRef}
        data-testid="timeline-track-element-lanes"
        className="relative flex w-full flex-col gap-1"
      >
        {isOverlayLayeringEnabled && <SnapLine verticalDragState={verticalDragState} />}

        {elementLanes.map((lane, laneIndex) => (
          <div
            key={`lane-${laneIndex}`}
            data-testid={`timeline-track-element-lane-${laneIndex}`}
            className="relative h-[30px] w-full"
          >
            {lane.map(element => {
              const isCurrentlySelected =
                (selectedElementData as ImageOverlaySelectionElementData)?.elementId === element.id;
              const isElementVerticallyDragging = Boolean(
                isOverlayLayeringEnabled && isVerticalDragging && draggingElementId === element.id
              );

              return (
                <TimelineTrackElement
                  key={`element-${laneIndex}-${element.id}`}
                  element={element}
                  laneIndex={laneIndex}
                  isVerticalDragging={isElementVerticallyDragging}
                  verticalDragState={
                    isOverlayLayeringEnabled && draggingElementId === element.id ? verticalDragState : null
                  }
                  isSelected={isCurrentlySelected}
                  onElementClick={handleElementClick}
                  handleElementUpdateCallback={handleElementUpdateCallback}
                  handleElementDeleteCallback={handleElementDeleteCallback}
                />
              );
            })}
          </div>
        ))}
      </div>
    </DndContext>
  );
}
